import { observer } from "mobx-react-lite";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useDelayedFlag, useTran } from "@/hooks";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PATHS } from "@/lib/router";

import { useFrameworkData, useKpiTargetsStore } from "../hooks";
import TargetSummaryTable from "../components/TargetSummaryTable";
import TargetSummarySkeleton from "../components/skeleton/TargetSummarySkeleton";

const TargetSummaryPage = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { loadingFrameworks } = useFrameworkData();
  const {
    activeCategory,
    loadingDisclosures,
    loadingTargets,
    selectedFramework,
    selectedYear,
    targetSearchTerm,
  } = useKpiTargetsStore();

  const isLoading = loadingFrameworks || loadingDisclosures || loadingTargets;
  const showSkeleton = useDelayedFlag(isLoading);
  const searchLabel = targetSearchTerm?.trim();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Target Summary")}
        description={t("Review saved targets for the selected filters.")}
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate(PATHS.ESG_TARGETS)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("Back")}
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">
          {t("Framework")}: {selectedFramework}
        </Badge>
        <Badge variant="outline">
          {t("Category")}: {activeCategory}
        </Badge>
        <Badge variant="outline">
          {t("Year")}: {selectedYear}
        </Badge>
        {searchLabel ? (
          <Badge variant="secondary">
            {t("Search")}: {searchLabel}
          </Badge>
        ) : null}
      </div>

      {showSkeleton ? <TargetSummarySkeleton /> : <TargetSummaryTable />}
    </div>
  );
});

export default TargetSummaryPage;
